"use client";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useEffect, useState } from "react";

import type {
  DeviceChannelCreateDto,
  DeviceChannelDto,
  DeviceDto,
  DeviceServiceRecordCreateDto,
  DeviceServiceRecordUpdateDto,
} from "@/lib/api/types";
import type { DeviceCreateDto, DeviceListFilters, DeviceUpdateDto } from "@/modules/devices/api";
import { deviceChannelsGateway, deviceQueryKeys, devicesGateway, otaGateway } from "@/modules/devices/api";
import type { OtaScheduleRequestDto, OtaUpdateRequestDto } from "@/lib/api/types/ota";
import { getDeviceAuditLog, recordDeviceAudit } from "@/lib/audit/device-audit-log";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { DeviceTableSortKey } from "@/modules/devices/components/device-table-columns";
import { useAuthStore } from "@/modules/auth/store";
import type { DevicePresence } from "@/store/realtime";
import { useRealtimeStore } from "@/store/realtime";

const PAGE_SIZE = 80;

export { useDebouncedValue } from "@/lib/hooks/use-debounced-value";

/**
 * Subscribes only to presence for one device. Other devices' WS updates do not re-render this hook
 * when the selected value is unchanged (Zustand `Object.is`).
 */
export function useDeviceLivePresence(deviceId: string): DevicePresence | null {
  return useRealtimeStore((s) => s.deviceStatus.get(deviceId) ?? null);
}

export function useDevicesInfinite(filters: DeviceListFilters, enabled: boolean) {
  const tenantId = useAuthStore((s) => s.tenantId);

  return useInfiniteQuery({
    queryKey: deviceQueryKeys.list(tenantId ?? "", filters),
    queryFn: ({ pageParam }) =>
      devicesGateway.listPage({
        tenantId: tenantId ?? "",
        limit: PAGE_SIZE,
        offset: pageParam,
        search: filters.search || undefined,
        status: filters.status || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: enabled && Boolean(tenantId),
    staleTime: 30_000,
  });
}

export function useDevice(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceQueryKeys.detail(deviceId),
    queryFn: () => devicesGateway.getById(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 15_000,
  });
}

export function useDeviceServiceRecords(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceQueryKeys.serviceRecords(deviceId),
    queryFn: () => devicesGateway.listServiceRecords(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 15_000,
  });
}

export function useDeviceServiceRecordPlatformUsers(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceQueryKeys.serviceRecordPlatformUsers(deviceId),
    queryFn: () => devicesGateway.listServiceRecordPlatformUsers(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 60_000,
  });
}

export function useCreateDeviceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DeviceCreateDto) => devicesGateway.create(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: data.id,
        action: "device.create",
        payload_summary: { device_uid: data.device_uid, name: data.name },
      });
    },
  });
}

export function useUpdateDeviceMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DeviceUpdateDto) => devicesGateway.update(deviceId, body),
    onSuccess: (data, body) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
      queryClient.setQueryData(deviceQueryKeys.detail(deviceId), data);
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "device.update",
        payload_summary: { patch_keys: Object.keys(body) },
      });
    },
  });
}

export function usePatchDeviceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, body }: { deviceId: string; body: DeviceUpdateDto }) =>
      devicesGateway.update(deviceId, body),
    onSuccess: (_, { deviceId, body }) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "device.patch",
        payload_summary: { patch_keys: Object.keys(body) },
      });
    },
  });
}

export function useDeleteDeviceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => devicesGateway.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
    },
  });
}

export function useFlattenedDevicePages(
  data: { pages: Array<{ items: DeviceDto[] }> } | undefined,
) {
  return useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
}

export type DeviceManagementListFilters = {
  search: string;
  status: string;
  statusScope: "active" | "inactive" | "";
  protocol: string;
  locationValue: string;
  locationKind: "site" | "city" | "state" | "";
  simType: "phone" | "industrial" | "other" | "";
  assignmentState: "assigned" | "unassigned" | "";
  assignedUserId: string;
  warrantyScope: "expiring_this_month" | "expired" | "next_30d" | "next_90d" | "unknown" | "";
  deviceCategoryId: string;
  alertPreset: "threshold_breached" | "";
  alertRangeKey: "last_24h" | "last_7d" | "custom" | "";
  alertStart: string;
  alertEnd: string;
  sort: DeviceTableSortKey | null;
  order: "asc" | "desc";
};

export const DEVICE_MANAGEMENT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEVICE_MANAGEMENT_DEFAULT_PAGE_SIZE = DEVICE_MANAGEMENT_PAGE_SIZE_OPTIONS[0];

export function useDeviceManagementList(
  filters: DeviceManagementListFilters,
  page: number,
  pageSize: number,
  tenantId: string | null,
  workspaceScope: "all" | null,
  enabled: boolean,
) {
  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const effective = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch],
  );

  return useQuery({
    queryKey: deviceQueryKeys.managementList(
      tenantId ?? "",
      effective.search,
      effective.status,
      effective.statusScope,
      effective.protocol,
      effective.locationKind,
      effective.locationValue,
      effective.simType,
      effective.assignmentState,
      effective.assignedUserId,
      effective.warrantyScope,
      effective.deviceCategoryId,
      effective.alertPreset,
      effective.alertRangeKey,
      effective.alertStart,
      effective.alertEnd,
      page,
      pageSize,
      effective.sort,
      effective.order,
      workspaceScope ?? "",
    ),
    queryFn: () =>
      devicesGateway.listPage({
        tenantId,
        workspaceScope,
        limit: pageSize,
        offset: page * pageSize,
        search: effective.search || undefined,
        status: effective.status || undefined,
        statusScope: effective.statusScope || undefined,
        protocol: effective.protocol || undefined,
        locationKind: effective.locationKind || undefined,
        locationValue: effective.locationValue || undefined,
        simType: effective.simType || undefined,
        assignmentState: effective.assignmentState || undefined,
        assignedUserId: effective.assignedUserId || undefined,
        warrantyScope: effective.warrantyScope || undefined,
        deviceCategoryId: effective.deviceCategoryId || undefined,
        alertPreset: effective.alertPreset || undefined,
        alertRangeKey: effective.alertRangeKey || undefined,
        alertStart: effective.alertStart || undefined,
        alertEnd: effective.alertEnd || undefined,
        sort: effective.sort,
        order: effective.order,
      }),
    enabled: enabled && (workspaceScope === "all" || Boolean(tenantId)),
    staleTime: 25_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useDeviceLocationSuggestions(
  query: string,
  tenantId: string | null,
  workspaceScope: "all" | null,
  enabled: boolean,
) {
  const debouncedQuery = useDebouncedValue(query, 250);

  return useInfiniteQuery({
    queryKey: [
      ...deviceQueryKeys.all,
      "location-suggestions",
      tenantId ?? "",
      workspaceScope ?? "",
      debouncedQuery,
    ] as const,
    queryFn: ({ pageParam }) =>
      devicesGateway.listLocationSuggestions({
        tenantId,
        workspaceScope,
        q: debouncedQuery || undefined,
        limit: 20,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: enabled && (workspaceScope === "all" || Boolean(tenantId)),
    staleTime: 30_000,
  });
}

export function useDeviceAssignedUserSuggestions(
  query: string,
  tenantId: string | null,
  workspaceScope: "all" | null,
  enabled: boolean,
) {
  const debouncedQuery = useDebouncedValue(query, 250);
  return useInfiniteQuery({
    queryKey: [
      ...deviceQueryKeys.all,
      "assigned-user-suggestions",
      tenantId ?? "",
      workspaceScope ?? "",
      debouncedQuery,
    ] as const,
    queryFn: ({ pageParam }) => devicesGateway.listAssignedUserSuggestions({
      tenantId,
      workspaceScope,
      q: debouncedQuery || undefined,
      limit: 20,
      offset: pageParam,
    }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    enabled: enabled && (workspaceScope === "all" || Boolean(tenantId)),
    staleTime: 30_000,
  });
}

export function useCreateDeviceServiceRecordMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DeviceServiceRecordCreateDto) => devicesGateway.createServiceRecord(deviceId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.serviceRecords(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
    },
  });
}

export function useUpdateDeviceServiceRecordMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, body }: { recordId: string; body: DeviceServiceRecordUpdateDto }) =>
      devicesGateway.updateServiceRecord(deviceId, recordId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.serviceRecords(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
    },
  });
}

export function useDeviceAccessSummary(enabled = true) {
  return useQuery({
    queryKey: [...deviceQueryKeys.all, "access-summary"],
    queryFn: () => devicesGateway.accessSummary(),
    enabled,
    staleTime: 30_000,
  });
}

export function useUpdateDeviceAssignmentsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, userIds, tenantId }: { deviceId: string; userIds: string[]; tenantId?: string | null }) =>
      devicesGateway.updateAssignments(deviceId, userIds, tenantId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
      queryClient.setQueryData(deviceQueryKeys.detail(data.id), data);
    },
  });
}

export function useOtaStatusQuery(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceQueryKeys.otaStatus(deviceId),
    queryFn: () => otaGateway.getStatus(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 10_000,
  });
}

export function useOtaHistoryQuery(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: deviceQueryKeys.otaHistory(deviceId),
    queryFn: () => otaGateway.getHistory(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 10_000,
  });
}

export function useOtaPushMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OtaUpdateRequestDto) => otaGateway.postUpdate(deviceId, body),
    onSuccess: (data, body) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.otaStatus(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.otaHistory(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "ota.push",
        payload_summary: {
          target_version: body.target_version,
          channel: body.channel ?? "stable",
          force: body.force ?? false,
          job_id: data.id,
          job_status: data.job_status,
        },
      });
    },
  });
}

export function useOtaScheduleMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OtaScheduleRequestDto) => otaGateway.postSchedule(deviceId, body),
    onSuccess: (data, body) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.otaHistory(deviceId) });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "ota.schedule",
        payload_summary: {
          target_version: body.target_version,
          scheduled_at: body.scheduled_at,
          job_id: data.id,
        },
      });
    },
  });
}

export function useOtaRollbackMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => otaGateway.postRollback(deviceId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.otaStatus(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.otaHistory(deviceId) });
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "ota.rollback",
        payload_summary: { job_id: data.id },
      });
    },
  });
}

export function useDeviceChannelsQuery(deviceId: string, enabled = true) {
  return useQuery({
    queryKey: [...deviceQueryKeys.detail(deviceId), "channels"] as const,
    queryFn: () => deviceChannelsGateway.list(deviceId),
    enabled: enabled && Boolean(deviceId),
    staleTime: 15_000,
  });
}

export function useCreateDeviceChannelMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DeviceChannelCreateDto) => deviceChannelsGateway.create(deviceId, body),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: [...deviceQueryKeys.detail(deviceId), "channels"] });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "channel.create",
        payload_summary: { channel_id: row.id, key: row.key },
      });
    },
  });
}

export function useUpdateDeviceChannelMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, body }: { channelId: string; body: Partial<DeviceChannelDto> }) =>
      deviceChannelsGateway.update(channelId, body),
    onSuccess: (_, { channelId, body }) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: [...deviceQueryKeys.detail(deviceId), "channels"] });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "channel.update",
        payload_summary: { channel_id: channelId, patch_keys: Object.keys(body) },
      });
    },
  });
}

export function useDeleteDeviceChannelMutation(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => deviceChannelsGateway.remove(channelId),
    onSuccess: (_, channelId) => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.detail(deviceId) });
      queryClient.invalidateQueries({ queryKey: [...deviceQueryKeys.detail(deviceId), "channels"] });
      const uid = useAuthStore.getState().user?.id ?? null;
      recordDeviceAudit({
        user_id: uid,
        device_id: deviceId,
        action: "channel.delete",
        payload_summary: { channel_id: channelId },
      });
    },
  });
}

export function useDeviceAuditLog(deviceId: string) {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const onAudit = (e: Event) => {
      const ce = e as CustomEvent<{ deviceId?: string }>;
      if (ce.detail?.deviceId === deviceId) {
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener("atech-device-audit", onAudit);
    return () => window.removeEventListener("atech-device-audit", onAudit);
  }, [deviceId]);
  return useMemo(() => getDeviceAuditLog(deviceId), [deviceId, version]);
}
